import { ReactElement, useRef, useEffect, useState, useMemo } from 'react';
import Plotly from 'plotly.js-dist-min';
import Spinner from '../Spinner';

interface TyreCompensationPlotProps {
  session: any;
  height?: number;
}

export default function TyreCompensationPlot({
  session,
  height = 400,
}: TyreCompensationPlotProps): ReactElement {
  const plotRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Get baseline config (letter_number === 0)
  const baselineConfig = useMemo(() => {
    if (!session?.configs) return null;
    return session.configs.find((config: any) => config.letter_number === 0);
  }, [session]);

  const plotData = useMemo(() => {
    if (!baselineConfig?.crr_temperature_plot) {
      return { traces: [], tableData: [] };
    }

    const { temperature, crr_at_temp } = baselineConfig.crr_temperature_plot;

    if (!temperature || !crr_at_temp || temperature.length === 0) {
      return { traces: [], tableData: [] };
    }

    // Create the line trace
    const traces = [
      {
        x: temperature,
        y: crr_at_temp,
        mode: 'lines',
        type: 'scatter',
        name: 'Temperature Model Crr',
        line: { color: '#8884d8', width: 2 },
        hovertemplate:
          'Temperature: %{x}°C<br>' +
          'Crr: %{y:.5f}<br>' +
          '<extra></extra>',
      },
    ];

    // Calculate table data for specific temperatures
    const targetTemps = [5, 10, 15, 20, 25, 30, 35, 40];
    const tableData: { temp: number; crr: number; percentDiff: number }[] = [];

    // Find Crr at 20°C for reference
    const crr20Index = temperature.findIndex((t: number) => t === 20);
    const crr20 = crr20Index >= 0 ? crr_at_temp[crr20Index] : null;

    targetTemps.forEach((temp) => {
      const index = temperature.findIndex((t: number) => t === temp);
      if (index >= 0 && crr20 !== null) {
        const crr = crr_at_temp[index];
        const percentDiff = ((crr - crr20) / crr20) * 100;
        tableData.push({ temp, crr, percentDiff });
      }
    });

    return { traces, tableData };
  }, [baselineConfig]);

  useEffect(() => {
    const plotElement = plotRef.current;
    if (!plotElement) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    if (plotData.traces.length > 0) {
      const layout = {
        title: { text: 'Tyre Temperature Compensation Model', font: { family: 'Poppins', size: 16 } },
        xaxis: {
          title: { text: 'Temperature (°C)', font: { family: 'Poppins' } },
          range: [0, 40],
        },
        yaxis: {
          title: { text: 'Temperature Model Crr', font: { family: 'Poppins' } },
        },
        showlegend: false,
        margin: { l: 80, r: 40, t: 60, b: 60 },
        hovermode: 'closest',
        plot_bgcolor: '#f8f9fa',
        paper_bgcolor: 'white',
        font: { family: 'Poppins' },
        height: height,
      };

      const config = {
        displayModeBar: true,
        modeBarButtonsToRemove: ['lasso2d', 'select2d'],
        displaylogo: false,
        responsive: true,
      };

      Plotly.newPlot(plotElement, plotData.traces, layout, config).then(() => {
        setIsLoading(false);
      });
    } else {
      Plotly.purge(plotElement);
      setIsLoading(false);
    }

    return () => {
      if (plotElement) {
        Plotly.purge(plotElement);
      }
    };
  }, [plotData, height]);

  if (!baselineConfig?.crr_temperature_plot) {
    return (
      <div className="cr-flex cr-items-center cr-justify-center cr-rounded-xl cr-bg-gray-50 cr-text-gray-500" style={{ height: `${height}px` }}>
        No temperature compensation data available
      </div>
    );
  }

  return (
    <div className="cr-space-y-4">
      {/* Plot */}
      <div className="cr-relative cr-rounded-xl cr-bg-clip-border cr-shadow-md" style={{ height: `${height}px` }}>
        {isLoading && (
          <div className="cr-absolute cr-inset-0 cr-z-10 cr-flex cr-items-center cr-justify-center cr-bg-white cr-bg-opacity-75">
            <Spinner isFetching={true} />
          </div>
        )}
        <div ref={plotRef} className="cr-h-full cr-w-full" />
      </div>

      {/* Table */}
      {plotData.tableData.length > 0 && (
        <div className="cr-overflow-x-auto">
          <table className="cr-min-w-full cr-divide-y cr-divide-gray-200 cr-rounded-lg cr-shadow">
            <thead className="cr-bg-gray-50">
              <tr>
                <th className="cr-px-6 cr-py-3 cr-text-center cr-text-xs cr-font-medium cr-text-gray-500 cr-uppercase cr-tracking-wider">
                  Temperature (°C)
                </th>
                <th className="cr-px-6 cr-py-3 cr-text-center cr-text-xs cr-font-medium cr-text-gray-500 cr-uppercase cr-tracking-wider">
                  Crr
                </th>
                <th className="cr-px-6 cr-py-3 cr-text-center cr-text-xs cr-font-medium cr-text-gray-500 cr-uppercase cr-tracking-wider">
                  % Difference to 20°C
                </th>
              </tr>
            </thead>
            <tbody className="cr-bg-white cr-divide-y cr-divide-gray-200">
              {plotData.tableData.map((row) => (
                <tr key={row.temp} className={row.temp === 20 ? 'cr-bg-blue-50' : ''}>
                  <td className="cr-px-6 cr-py-3 cr-whitespace-nowrap cr-text-sm cr-text-gray-900 cr-text-center cr-font-medium">
                    {row.temp}
                  </td>
                  <td className="cr-px-6 cr-py-3 cr-whitespace-nowrap cr-text-sm cr-text-gray-700 cr-text-center">
                    {row.crr.toFixed(5)}
                  </td>
                  <td className="cr-px-6 cr-py-3 cr-whitespace-nowrap cr-text-sm cr-text-center">
                    <span
                      className={
                        row.percentDiff < 0
                          ? 'cr-text-green-600 cr-font-medium'
                          : row.percentDiff > 0
                          ? 'cr-text-red-600 cr-font-medium'
                          : 'cr-text-gray-700'
                      }
                    >
                      {row.percentDiff >= 0 ? '+' : ''}
                      {row.percentDiff.toFixed(1)}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
